/**
 * Pinia store for the Studio "My Pitches" dashboard list.
 * Stale-while-revalidate: hydrate from localStorage on mount,
 * silent background refresh merges live data without blocking UI.
 */

import { defineStore } from "pinia";
import { ref } from "vue";
import {
  getActiveListingForPitch,
  getCreatorByUserId,
  getPitchAsset,
  getPitchRatings,
  listPitchesForCreator,
  type PitchRating,
} from "../lib/appwrite";
import {
  clearCachedPitches,
  loadCachedPitches,
  mergePitches,
  saveCachedPitches,
  toDashboardPitch,
  type DashboardPitch,
} from "./pitches-cache";
import { toUserFacingError, type UserFacingError } from "../lib/user-facing-error";

export const usePitchesStore = defineStore("pitches", () => {
  const activePitches = ref<DashboardPitch[]>([]);
  const archivedPitches = ref<DashboardPitch[]>([]);
  const ratings = ref<Map<string, PitchRating>>(new Map());
  const assetUrls = ref<Map<string, string>>(new Map());
  const loading = ref(false);
  const refreshInFlight = ref(false);
  const refreshError = ref<UserFacingError | null>(null);
  const error = ref<UserFacingError | null>(null);
  const hasCachedData = ref(false);
  const lastRefreshedAt = ref<number | null>(null);
  const cacheUserId = ref<string | null>(null);

  let refreshPromise: Promise<void> | null = null;

  function persistCache(): void {
    if (!cacheUserId.value) return;
    saveCachedPitches(
      cacheUserId.value,
      activePitches.value,
      archivedPitches.value,
      ratings.value,
      assetUrls.value,
    );
    lastRefreshedAt.value = Date.now();
  }

  function hydrateFromCache(creatorUserId: string): boolean {
    cacheUserId.value = creatorUserId;
    const cached = loadCachedPitches(creatorUserId);
    if (!cached || (cached.active.length === 0 && cached.archived.length === 0)) {
      hasCachedData.value = false;
      return false;
    }
    activePitches.value = cached.active;
    archivedPitches.value = cached.archived;
    ratings.value = cached.ratings;
    assetUrls.value = cached.assetUrls;
    hasCachedData.value = true;
    loading.value = false;
    if (cached.savedAt) {
      const parsed = Date.parse(cached.savedAt);
      if (Number.isFinite(parsed)) lastRefreshedAt.value = parsed;
    }
    return true;
  }

  async function enrichPitches(docs: Record<string, unknown>[]): Promise<DashboardPitch[]> {
    return Promise.all(
      docs.map(async (doc) => {
        const pitch = toDashboardPitch(doc);
        if (pitch.status === "live" && pitch.$id) {
          const listing = await getActiveListingForPitch(pitch.$id);
          if (listing && listing.expiresAt != null) {
            pitch.expiresAt = listing.expiresAt as string | number;
          }
        }
        return pitch;
      }),
    );
  }

  async function loadThumbnails(): Promise<void> {
    const ids = new Set<string>();
    for (const p of [...activePitches.value, ...archivedPitches.value]) {
      if (p.thumbnailFileId) ids.add(p.thumbnailFileId);
      if (p.ogImageFileId) ids.add(p.ogImageFileId);
    }
    if (ids.size === 0) {
      if (!hasCachedData.value) assetUrls.value = new Map();
      return;
    }
    const toFetch = [...ids].filter((id) => {
      const existing = assetUrls.value.get(id);
      return !existing || existing.length === 0;
    });
    if (toFetch.length === 0) return;
    const pairs = await Promise.all(
      toFetch.map(async (id) => {
        const row = await getPitchAsset(id);
        return [id, row?.cdnUrl ?? ""] as const;
      }),
    );
    const next = new Map(assetUrls.value);
    for (const [id, url] of pairs) {
      if (typeof url === "string" && url.length > 0) next.set(id, url);
    }
    assetUrls.value = next;
  }

  async function loadRatings(): Promise<void> {
    const liveIds: string[] = [];
    for (const p of activePitches.value) {
      if (p.status === "live" && p.$id) liveIds.push(p.$id);
    }
    for (const p of archivedPitches.value) {
      if (p.status === "live" && p.$id) liveIds.push(p.$id);
    }
    if (liveIds.length === 0) {
      if (!hasCachedData.value) ratings.value = new Map();
      return;
    }
    try {
      const map = await getPitchRatings(liveIds);
      ratings.value = new Map(map);
    } catch {
      if (!hasCachedData.value) ratings.value = new Map();
    }
  }

  async function loadActive(creatorId: string, merge: boolean): Promise<void> {
    const res = await listPitchesForCreator(creatorId);
    const incoming = await enrichPitches(res.documents as Record<string, unknown>[]);
    activePitches.value = merge
      ? mergePitches(activePitches.value, incoming)
      : incoming;
  }

  async function loadArchived(creatorId: string, merge: boolean): Promise<void> {
    const res = await listPitchesForCreator(creatorId, undefined, "archived");
    const incoming = await enrichPitches(res.documents as Record<string, unknown>[]);
    archivedPitches.value = merge
      ? mergePitches(archivedPitches.value, incoming)
      : incoming;
  }

  async function refreshAll(
    creatorUserId: string,
    opts: { force?: boolean } = {},
  ): Promise<void> {
    if (!creatorUserId) return;
    if (refreshPromise) return refreshPromise;

    cacheUserId.value = creatorUserId;
    const shouldMerge = hasCachedData.value;
    if (!shouldMerge) {
      loading.value = true;
    }
    error.value = null;
    refreshError.value = null;
    refreshInFlight.value = true;

    refreshPromise = (async () => {
      try {
        const creator = await getCreatorByUserId(creatorUserId);
        if (!creator) {
          if (!hasCachedData.value) {
            error.value = {
              headline: "Creator profile not found. Please contact support.",
            };
          }
          return;
        }
        const creatorId = creator.$id as string;
        await Promise.all([
          loadActive(creatorId, shouldMerge),
          loadArchived(creatorId, shouldMerge),
        ]);
        await loadThumbnails();
        await loadRatings();
        persistCache();
        hasCachedData.value = true;
      } catch (err: unknown) {
        const facing = toUserFacingError(err);
        if (hasCachedData.value) {
          refreshError.value = facing;
        } else {
          error.value = facing;
        }
      } finally {
        refreshInFlight.value = false;
        loading.value = false;
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }

  function clearCache(): void {
    clearCachedPitches(cacheUserId.value);
    cacheUserId.value = null;
    activePitches.value = [];
    archivedPitches.value = [];
    ratings.value = new Map();
    assetUrls.value = new Map();
    loading.value = false;
    refreshInFlight.value = false;
    refreshError.value = null;
    error.value = null;
    hasCachedData.value = false;
    lastRefreshedAt.value = null;
    refreshPromise = null;
  }

  function onPitchCreated(pitch: DashboardPitch): void {
    if (pitch.visibility === "archived") {
      archivedPitches.value = mergePitches(archivedPitches.value, [pitch]);
    } else {
      activePitches.value = mergePitches(activePitches.value, [pitch]);
    }
    persistCache();
  }

  function onPitchUpdated(pitch: Partial<DashboardPitch> & { $id: string }): void {
    const updateList = (list: DashboardPitch[]) =>
      list.map((p) => (p.$id === pitch.$id ? { ...p, ...pitch } : p));
    activePitches.value = updateList(activePitches.value);
    archivedPitches.value = updateList(archivedPitches.value);
    persistCache();
  }

  function onPitchPurged(pitchId: string): void {
    activePitches.value = activePitches.value.filter((p) => p.$id !== pitchId);
    archivedPitches.value = archivedPitches.value.filter((p) => p.$id !== pitchId);
    ratings.value.delete(pitchId);
    persistCache();
  }

  function onPitchArchived(pitchId: string): void {
    const pitch = activePitches.value.find((p) => p.$id === pitchId);
    activePitches.value = activePitches.value.filter((p) => p.$id !== pitchId);
    if (pitch) {
      archivedPitches.value = mergePitches(archivedPitches.value, [
        { ...pitch, visibility: "archived" },
      ]);
    }
    persistCache();
  }

  function onPitchReactivated(pitchId: string): void {
    const pitch = archivedPitches.value.find((p) => p.$id === pitchId);
    archivedPitches.value = archivedPitches.value.filter((p) => p.$id !== pitchId);
    if (pitch) {
      activePitches.value = mergePitches(activePitches.value, [
        { ...pitch, visibility: "public" },
      ]);
    }
    persistCache();
  }

  return {
    activePitches,
    archivedPitches,
    ratings,
    assetUrls,
    loading,
    refreshInFlight,
    refreshError,
    error,
    hasCachedData,
    lastRefreshedAt,
    hydrateFromCache,
    refreshAll,
    clearCache,
    onPitchCreated,
    onPitchUpdated,
    onPitchPurged,
    onPitchArchived,
    onPitchReactivated,
  };
});

export type { DashboardPitch };
